import { useContext, useState, useEffect } from "react";
import { supabase } from "../../supabase";
import { AuthContext } from "../../context/AuthContext";
import { useHistory, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AuthSplit from "../../components/AuthSplit";

const EyeIcon = ({ open }) =>
  open ? (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3l18 18" />
      <path d="M10.58 10.58A3 3 0 0 0 12 15a3 3 0 0 0 1.42-.42" />
      <path d="M9.88 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a18.6 18.6 0 0 1-3.17 4.57" />
      <path d="M6.61 6.61A18.6 18.6 0 0 0 1 12s4 8 11 8a10.94 10.94 0 0 0 4.76-1.06" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

// 비밀번호 칸 + 보기 버튼
function PwField({ id, label, placeholder, value, onChange, show, onToggle, autoComplete }) {
  return (
    <div className="ca-field">
      <label className="ca-label" htmlFor={id}>{label}</label>
      <div className="password-wrapper">
        <input
          id={id}
          className="auth-input"
          type={show ? "text" : "password"}
          autoComplete={autoComplete}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <button type="button" className="password-toggle" onClick={onToggle} aria-label="비밀번호 보기">
          <EyeIcon open={show} />
        </button>
      </div>
    </div>
  );
}

export default function UpdatePasswordPage() {
  const { user } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [showNewPw2, setShowNewPw2] = useState(false);
  const [done, setDone] = useState(false);
  const { t } = useTranslation();
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // 이메일 리셋 링크로 들어온 경우 감지
  const isReset = new URLSearchParams(location.search).get("reset") === "1";

  useEffect(() => {
    supabase.auth.exchangeCodeForSession(window.location.href);
  }, []);

  async function handleUpdate() {
    if (!isReset && !currentPassword) {
      setMsg("현재 비밀번호를 입력하세요.");
      return;
    }
    if (!newPassword) {
      setMsg("새 비밀번호를 입력하세요.");
      return;
    }
    if (newPassword !== newPassword2) {
      setMsg(t("auth_page.password_mismatch"));
      return;
    }

    setLoading(true);
    setMsg("");

    // 기존 비밀번호 재인증 (리셋이 아닌 경우만)
    if (!isReset) {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (signInError) {
        setMsg("현재 비밀번호가 올바르지 않습니다.");
        setLoading(false);
        return;
      }
    }

    // 비밀번호 변경
    const { error: updateError } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (updateError) {
      setMsg("비밀번호 변경 실패: " + updateError.message);
      setLoading(false);
      return;
    }

    setDone(true);
    setMsg("비밀번호가 성공적으로 변경되었습니다.");
    setTimeout(() => {
      window.location.href = "/#/mypage";
    }, 1500);
  }

  // 마이페이지 "비밀번호 변경"은 원래 가운데 카드 배치 그대로, 색만 사이트에 맞춤 (2026-10-19 김현비). 확인 칸 추가.
  if (!isReset) {
    const field = (value, setValue, show, setShow, placeholder, ac) => (
      <div className="password-wrapper">
        <input
          className="auth-input"
          type={show ? "text" : "password"}
          autoComplete={ac}
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <button type="button" className="password-toggle" onClick={() => setShow((v) => !v)} aria-label="비밀번호 보기">
          <EyeIcon open={show} />
        </button>
      </div>
    );
    return (
      <div className="ca-auth ca-single">
        <div className="auth-card">
          <div className="auth-title">비밀번호 변경</div>

          <div className="auth-form">
            {msg && <div className="auth-error-text">{msg}</div>}
            {field(currentPassword, setCurrentPassword, showCurrentPw, setShowCurrentPw, "현재 비밀번호", "current-password")}
            {field(newPassword, setNewPassword, showNewPw, setShowNewPw, "새 비밀번호", "new-password")}
            {field(newPassword2, setNewPassword2, showNewPw2, setShowNewPw2, "새 비밀번호 확인", "new-password")}

            <button className="auth-btn" onClick={handleUpdate} disabled={loading}>
              비밀번호 변경
            </button>
          </div>

          <div className="auth-links">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                history.push("/mypage");
              }}
            >
              ← 마이페이지로 돌아가기
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthSplit
      hero1={t("auth_split.reset_hero1")}
      hero2={t("auth_split.reset_hero2")}
      lead={isReset ? t("auth_split.reset_lead") : t("auth_split.change_lead")}
      title={isReset ? t("auth_split.reset_title") : t("auth_split.change_title")}
      sub={isReset ? t("auth_split.reset_sub") : t("auth_split.change_sub")}
    >
      {msg && <div className={done ? "auth-success-text" : "auth-error-text"}>{msg}</div>}

      <form
        className="auth-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!loading) handleUpdate();
        }}
      >
        {/* 현재 비밀번호 (리셋이 아닌 경우만) */}
        {!isReset && (
          <PwField
            id="cur-pw"
            label={t("auth_split.current_pw")}
            placeholder={t("auth_split.pw_placeholder")}
            value={currentPassword}
            onChange={setCurrentPassword}
            show={showCurrentPw}
            onToggle={() => setShowCurrentPw((v) => !v)}
            autoComplete="current-password"
          />
        )}
        <PwField
          id="new-pw"
          label={t("auth_split.new_pw")}
          placeholder={t("auth_split.pw_placeholder")}
          value={newPassword}
          onChange={setNewPassword}
          show={showNewPw}
          onToggle={() => setShowNewPw((v) => !v)}
          autoComplete="new-password"
        />
        <PwField
          id="new-pw2"
          label={t("auth_split.new_pw_confirm")}
          placeholder={t("auth_split.retype")}
          value={newPassword2}
          onChange={setNewPassword2}
          show={showNewPw2}
          onToggle={() => setShowNewPw2((v) => !v)}
          autoComplete="new-password"
        />

        <button className="auth-btn" type="submit" disabled={loading}>
          {t("auth_split.change_btn")}
        </button>
      </form>

      <div className="auth-links">
        {isReset ? (
          <Link to="/login">{t("auth_page.back_to_login")}</Link>
        ) : (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              history.push("/mypage");
            }}
          >
            {t("auth_split.back_mypage")}
          </a>
        )}
      </div>
    </AuthSplit>
  );
}
